import { Card, Menu, useMantineTheme } from '@mantine/core';
import { captureException } from '@sentry/react';
import { useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { Icon } from '@/components';
import { analysisApi } from '@/features/analysisApi';

import { getWorklogStatusText } from '@/features/types';
import { GoogleEvents } from '@/features/googleEvents';
import {
  deleteWorklogItem,
  setWorklogPage,
  updateWorklogStatus,
  useDeleteWorkLogMutation,
  worklogApi,
} from '@/features/worklogSlice';
import {
  openWorklogModal,
  WorklogModalMode,
} from '@/features/worklogModalSlice';
import classes from './WorklogActivity.module.css';
import { parseNoteSections } from './noteSections';
import TagManager from 'react-gtm-module';
import { fieldApi } from '@/features/fieldSlice';
import {
  formatDateAsPerLocale,
  formatDateTimeAsPerLocale,
} from '@/helpers/date';
import { Check, Edit, Trash2, X } from 'react-feather';
import { type WorkLogResponse as WorkLogData } from '@/generated/types/navigator/types.gen';
import { useConfirm } from '@/components/Modals/useConfirm';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import { notifications } from '@mantine/notifications';

export interface WorklogActivityProps {
  worklog: WorkLogData;
  itemRef?: React.Ref<HTMLDivElement>;
}

export const WorklogActivity = ({ worklog, itemRef }: WorklogActivityProps) => {
  const theme = useMantineTheme();
  const [deleteWorklog, { isLoading: isDeleting }] = useDeleteWorkLogMutation();
  const { selectedItem } = useAppSelector((s) => s.analysisMap);
  const dispatch = useAppDispatch();
  const confirm = useConfirm();
  const intl = useClientTerminology();

  const noteSections = useMemo(
    () => (worklog.notes ? parseNoteSections(worklog.notes) : []),
    [worklog.notes]
  );

  // A partially populated source means an upstream sync problem, so the activity
  // stays editable until both the source and its sync time are present.
  const syncDateTime = useMemo(() => {
    if (
      worklog.source &&
      worklog.source != 'internal' &&
      worklog.source_metadata?.sync_timestamp
    ) {
      return worklog.source_metadata.sync_timestamp;
    }
  }, [worklog.source, worklog.source_metadata?.sync_timestamp]);

  useEffect(() => {
    if (selectedItem && Object.keys(selectedItem).length) {
      dispatch(setWorklogPage(1));
      dispatch(worklogApi.util.invalidateTags([{ type: 'worklogData' }]));
      dispatch(updateWorklogStatus(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem]);

  const handleDeleteWorklogActivity = useCallback(async () => {
    const notificationId = notifications.show({
      title: intl.formatMessage({ id: 'worklog.activityDeletingTitle' }),
      message: intl.formatMessage({ id: 'worklog.activityDeletingMessage' }),
      loading: true,
      autoClose: false,
      withCloseButton: false,
      color: 'teal',
    });

    try {
      await deleteWorklog(worklog.id).unwrap();

      dispatch(
        deleteWorklogItem({
          id: worklog.id,
          level3: worklog.entity_id,
        })
      );
      notifications.update({
        id: notificationId,
        color: 'teal',
        title: intl.formatMessage({ id: 'action.success' }),
        message: intl.formatMessage({ id: 'worklog.activityDeleteSuccess' }),
        loading: false,
        icon: <Icon Component={Check} size={16} color="white" />,
        autoClose: 2000,
      });
      dispatch(
        analysisApi.util.invalidateTags([
          { type: 'itemData', id: worklog.entity_id },
        ])
      );
      dispatch(fieldApi.util.invalidateTags([{ type: 'fieldWorkStatus' }])); // Adding this to invalidate the field api to retrieve the current workstatus fields after deleting a worklog

      TagManager.dataLayer({
        dataLayer: {
          event: GoogleEvents.worklogDeleted,
        },
      });
    } catch (error) {
      notifications.update({
        id: notificationId,
        color: 'red',
        title: intl.formatMessage({ id: 'action.error' }),
        message: intl.formatMessage({ id: 'worklog.activityDeleteError' }),
        loading: false,
        icon: <Icon Component={X} size={16} color="white" />,
        autoClose: 5000,
      });
      captureException(error);
    }
    dispatch(updateWorklogStatus(false));
  }, [intl, dispatch, deleteWorklog, worklog.id, worklog.entity_id]);

  return (
    <Card
      withBorder
      shadow="none"
      radius={theme.radius.sm}
      padding={theme.other.spacing.sm}
      className={classes.cardRoot}
      ref={itemRef}
      data-testid="worklog-activity-item"
    >
      <Card.Section withBorder={false} inheritPadding py="sm">
        <div
          className={classes.worklogCard}
          style={{
            gap: theme.other.spacing.xs,
          }}
        >
          <div
            className={classes.worklogCardItem}
            style={{ gap: theme.other.spacing.xs }}
          >
            <div
              className={classes.worklogCardDate}
              style={{
                ...theme.other.typography.variants.bodySmallRegular,
                color: theme.other.mainColor.foreground.secondary,
              }}
            >
              {formatDateTimeAsPerLocale(worklog.date)}
            </div>
            <div
              style={{
                ...theme.other.typography.variants.bodyRegular,
              }}
            >
              {getWorklogStatusText(worklog.status)}
            </div>
            {noteSections.map((section) => (
              <div key={section.heading ?? 'notes'}>
                {section.heading && (
                  <div
                    style={{
                      ...theme.other.typography.variants.h7,
                      fontFamily: theme.other.typography.fontFamily.primary,
                      color: theme.other.mainColor.foreground.secondary,
                    }}
                  >
                    {section.heading}
                  </div>
                )}
                <div
                  style={{
                    ...theme.other.typography.variants.bodySmallRegular,
                    color: theme.other.mainColor.foreground.secondary,
                    whiteSpace: 'pre-line',
                  }}
                >
                  {section.body}
                </div>
              </div>
            ))}
            {worklog.program_name && (
              <div
                style={{
                  ...theme.other.typography.variants.caption,
                  color: theme.other.mainColor.foreground.tertiary,
                }}
              >
                {intl.formatMessage(
                  { id: 'worklog.program' },
                  { name: worklog.program_name }
                )}
              </div>
            )}
            {syncDateTime && (
              <div
                style={{
                  ...theme.other.typography.variants.caption,
                  color: theme.other.mainColor.foreground.tertiary,
                }}
              >
                {intl.formatMessage(
                  { id: 'worklog.syncedFromSource' },
                  {
                    source: worklog.source,
                    date: formatDateAsPerLocale(syncDateTime),
                  }
                )}
              </div>
            )}
          </div>
          {!syncDateTime && (
            <Menu
              withinPortal
              position="bottom-end"
              shadow="sm"
              id="menu"
              classNames={{
                dropdown: classes.dropdown,
              }}
            >
              <Menu.Target>
                <div
                  className={classes.actionButton}
                  data-testid="worklog-activity-actions-button"
                >
                  <Icon name="ActionDots" size={24} />
                </div>
              </Menu.Target>
              <Menu.Dropdown id="worklog-activity">
                <Menu.Item
                  leftSection={<Icon Component={Edit} size={16} />}
                  onClick={() => {
                    dispatch(
                      openWorklogModal({
                        worklogData: worklog,
                        mode: WorklogModalMode.EDIT,
                        spanId: worklog.entity_id || '',
                      })
                    );
                  }}
                >
                  {intl.formatMessage({
                    id: 'action.edit',
                  })}
                </Menu.Item>
                <Menu.Item
                  leftSection={<Icon Component={Trash2} size={16} />}
                  disabled={isDeleting}
                  data-testid="worklog-activity-delete-button"
                  onClick={async () => {
                    const { confirmed } = await confirm({
                      titleId: 'action.delete',
                      contentId: 'worklog.activityDeleteConfirmation',
                      primaryLabelId: 'action.delete',
                      secondaryLabelId: 'action.cancel',
                      danger: true,
                    });

                    if (!confirmed) return;
                    handleDeleteWorklogActivity();
                  }}
                >
                  {intl.formatMessage({
                    id: 'action.delete',
                  })}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}
        </div>
      </Card.Section>
    </Card>
  );
};
